import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as fabric from "fabric";
import { Type, Hash, Trash2, Save, ArrowLeft } from "lucide-react";
import { api, ApiError } from "../lib/api.js";
import { Button } from "../components/ui/button.js";
import { Card, CardContent } from "../components/ui/card.js";
import { Input } from "../components/ui/input.js";

const CANVAS_W = 800;
const CANVAS_H = 566; // ~A4 landscape ratio

/**
 * Drag-and-drop certificate designer (REQ-1.3). Fabric.js canvas with static
 * text, dynamic field tokens (recipient name / issue date), and a background
 * color. Output persists to `templates.canvas` JSONB; the render pipeline (M6)
 * substitutes the tokens per recipient.
 */
export function DesignerPage() {
  const { slug = "", wsId = "", templateId } = useParams();
  const navigate = useNavigate();
  const canvasElRef = useRef<HTMLCanvasElement | null>(null);
  const fabricRef = useRef<fabric.Canvas | null>(null);
  const [name, setName] = useState("Untitled certificate");
  const [bg, setBg] = useState("#ffffff");
  const [saved, setSaved] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!canvasElRef.current) return;
    const canvas = new fabric.Canvas(canvasElRef.current, {
      width: CANVAS_W,
      height: CANVAS_H,
      backgroundColor: bg,
    });
    fabricRef.current = canvas;

    if (templateId) {
      void api.listTemplates(slug, wsId).then(({ templates }) => {
        const tpl = templates.find((t) => t.id === templateId);
        if (!tpl) return;
        setName(tpl.name);
        void canvas.loadFromJSON(tpl.canvas).then(() => canvas.requestRenderAll());
      });
    }

    return () => {
      void canvas.dispose();
      fabricRef.current = null;
    };
  }, [slug, wsId, templateId]);

  function addText(text: string, dynamicField?: string) {
    const canvas = fabricRef.current;
    if (!canvas) return;
    const box = new fabric.Textbox(text, {
      left: 100,
      top: 100,
      fontSize: 28,
      fontFamily: "Inter, sans-serif",
      fill: "#1c1917",
    });
    (box as unknown as { dynamicField?: string }).dynamicField = dynamicField;
    canvas.add(box);
    canvas.setActiveObject(box);
    canvas.requestRenderAll();
  }

  function deleteSelected() {
    const canvas = fabricRef.current;
    const obj = canvas?.getActiveObject();
    if (canvas && obj) {
      canvas.remove(obj);
      canvas.requestRenderAll();
    }
  }

  function changeBg(color: string) {
    setBg(color);
    const canvas = fabricRef.current;
    if (canvas) {
      canvas.backgroundColor = color;
      canvas.requestRenderAll();
    }
  }

  async function save() {
    const canvas = fabricRef.current;
    if (!canvas) return;
    setError(null);
    try {
      const canvasJson = canvas.toObject(["dynamicField"]) as unknown as Record<string, unknown>;
      const res = await api.saveTemplate(slug, wsId, {
        id: templateId,
        name,
        type: "certificate",
        canvas: canvasJson,
        fields: {},
      });
      setSaved(`Saved "${res.template.name}".`);
      if (!templateId) {
        navigate(`/issuers/${slug}/workspaces/${wsId}/designer/${res.template.id}`, { replace: true });
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Save failed");
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft size={16} />
        </Button>
        <Input className="max-w-xs" value={name} onChange={(e) => setName(e.target.value)} />
        <Button className="ml-auto" onClick={() => void save()}>
          <Save size={16} /> Save
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" size="sm" onClick={() => addText("Text")}>
          <Type size={16} /> Text
        </Button>
        <Button variant="secondary" size="sm" onClick={() => addText("{{ recipient.name }}", "recipient.name")}>
          <Hash size={16} /> Recipient name
        </Button>
        <Button variant="secondary" size="sm" onClick={() => addText("{{ issueDate }}", "issueDate")}>
          <Hash size={16} /> Issue date
        </Button>
        <label className="inline-flex h-8 cursor-pointer items-center gap-1 rounded-[var(--radius)] border border-input bg-card px-3 text-sm font-medium">
          Background
          <input type="color" value={bg} onChange={(e) => changeBg(e.target.value)} className="ml-1 h-4 w-6 border-0 bg-transparent p-0" />
        </label>
        <Button variant="secondary" size="sm" onClick={deleteSelected}>
          <Trash2 size={16} /> Delete
        </Button>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
      {saved && <p className="text-sm text-[var(--color-success)]">{saved}</p>}

      <Card className="inline-block">
        <CardContent className="p-3">
          <canvas ref={canvasElRef} className="rounded border border-border" />
        </CardContent>
      </Card>
    </div>
  );
}
